import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { request } from '../api/client'

interface Suppression {
  id: string
  hostname: string
  justification: string
  expires_at: string
  created_by: string
  hit_count: number
  active: boolean
}

export interface RuleHistoryData {
  rule_id: string
  rule_title: string
  window_days: number
  total: number
  open_now: number
  hosts: { hostname: string; count: number }[]
  daily: { day: string; count: number }[]
  dispositions: Record<string, number>
  lifecycle: {
    status: string
    tracked: boolean
    backtested_at: string | null
    running: boolean
    disabled_reason: string
  }
  suppressions: Suppression[]
  notes: string[]
}

const DISPOSITION: Record<string, string> = {
  true_positive: 'real',
  benign_positive: 'expected activity',
  false_positive: 'false positive',
  duplicate: 'duplicate',
}

/**
 * What this rule has done before.
 *
 * Triage is a judgement about one alert made almost entirely on evidence about
 * everything around it: "nginx spawned a shell" is a page at 03:00 the first time and
 * a deployment hook the fortieth. Without this panel that difference lives in one
 * analyst's memory, and the next person on call pays for it again.
 *
 * The panel never says "this is noise". It says what happened the last thirty times
 * and who decided what — the decision stays with the person, because a console that
 * pre-judges alerts is a console that will eventually pre-judge the real one.
 *
 * Loaded separately from the alert, and failing separately: a thirty-day aggregate
 * that is slow must not take the alert's own story off the screen.
 */
export function RuleHistory({ ruleId }: { ruleId: string }) {
  const query = useQuery({
    queryKey: ['rule-history', ruleId],
    queryFn: () => request<RuleHistoryData>(`/rules/${encodeURIComponent(ruleId)}/history/`),
    retry: false,
  })

  if (query.isError)
    return (
      <p className="muted" style={{ margin: 0 }}>
        This rule's history could not be loaded, so there is nothing here to compare this alert
        against. The alert above is unaffected.
      </p>
    )
  if (!query.data) return <div className="skeleton" />

  const data = query.data
  const peak = Math.max(1, ...data.daily.map((day) => day.count))
  const dispositions = Object.entries(data.dispositions)

  return (
    <>
      <p style={{ marginTop: 0 }}>
        <strong>{data.total}</strong> alert{data.total === 1 ? '' : 's'} from this rule in the last{' '}
        {data.window_days} days
        {data.open_now > 0 ? `, ${data.open_now} still open` : ''}.
      </p>

      {data.notes.length > 0 && (
        <ul style={{ paddingLeft: 'var(--space-4)', margin: '0 0 var(--space-3)' }}>
          {data.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}

      {data.total > 0 && (
        <figure style={{ margin: '0 0 var(--space-3)' }}>
          <svg
            viewBox={`0 0 ${data.daily.length} 20`}
            preserveAspectRatio="none"
            style={{ width: '100%', height: 44 }}
            role="img"
            aria-label={`${data.total} alerts over ${data.window_days} days, busiest day ${peak}`}
          >
            {data.daily.map((day, index) => (
              <rect
                key={day.day}
                x={index}
                y={20 - (day.count / peak) * 20}
                width={0.8}
                height={(day.count / peak) * 20}
                fill="var(--accent)"
              >
                <title>
                  {day.day}: {day.count}
                </title>
              </rect>
            ))}
          </svg>
          <figcaption className="muted" style={{ fontSize: 'var(--text-xs)' }}>
            Per day over {data.window_days} days — busiest day {peak}
          </figcaption>
        </figure>
      )}

      {dispositions.length > 0 && (
        <p className="muted" style={{ fontSize: 'var(--text-xs)' }}>
          Closed as:{' '}
          {dispositions
            .map(([name, count]) => `${count} ${DISPOSITION[name] ?? name}`)
            .join(', ')}
        </p>
      )}

      {data.hosts.length > 0 && (
        <ul className="muted" style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: 'var(--text-xs)' }}>
          {data.hosts.slice(0, 5).map((host) => (
            <li key={host.hostname}>
              {host.hostname} — {host.count}
            </li>
          ))}
        </ul>
      )}

      {data.suppressions.map((suppression) => (
        <p
          key={suppression.id}
          className="muted"
          style={{ fontSize: 'var(--text-xs)', marginBottom: 0 }}
        >
          {suppression.active ? 'Suppressed' : 'Was suppressed'} on {suppression.hostname} by{' '}
          {suppression.created_by} — {suppression.justification} ({suppression.hit_count} hidden)
        </p>
      ))}

      <Link
        to={`/queue?rule_id=${encodeURIComponent(data.rule_id)}&status=closed`}
        style={{ fontSize: 'var(--text-xs)' }}
      >
        See what was decided about these →
      </Link>
    </>
  )
}
